import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";

export default class extends Controller {
  static targets = [
    "dropdown",
    "triggerLabel",
    "startDateInput",
    "endDateInput",
    "compareStartDateInput",
    "compareEndDateInput",
    "calendar",
    "compareSection",
    "compareTypeSection",
    "presetItem",
    "compareTypeItem",
    "form",
    "dateModeItem",
    "filterItem",
  ];

  static values = {
    preset: { type: String, default: "last_28_days" },
    startDate: String,
    endDate: String,
    compareEnabled: { type: Boolean, default: false },
    compareType: { type: String, default: "previous_period" },
    compareStartDate: String,
    compareEndDate: String,
    dateMode: { type: String, default: "reservation_date" },
  };

  connect() {
    this.initializeFlatpickr();
    this.updatePresetHighlight();
    this.updateCompareTypeHighlight();
    this.updateCompareVisibility();
    this.updateDateModeHighlight();

    // Initialize extra filter values from server-rendered active items
    this.filterValues = {};
    this.filterItemTargets.forEach((item) => {
      const name = item.dataset.filterName;
      if (item.classList.contains("active") && !(name in this.filterValues)) {
        this.filterValues[name] = item.dataset.filterValue;
      }
    });
    this.updateTriggerLabel();

    // Close dropdown when clicking outside
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.boundHandleClickOutside);
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleClickOutside);
    this.destroyFlatpickr();
  }

  initializeFlatpickr() {
    const commonOptions = {
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "M j, Y",
      allowInput: false,
      disableMobile: true,
    };

    // Main start date picker
    if (this.hasStartDateInputTarget) {
      this.startPicker = flatpickr(this.startDateInputTarget, {
        ...commonOptions,
        defaultDate: this.startDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.startDateValue = this.formatDate(selectedDates[0]);
            this.presetValue = "custom";
            this.updatePresetHighlight();
            this.updateCalendar();
            this.updateComparisonDates();
          }
        },
      });
    }

    // Main end date picker
    if (this.hasEndDateInputTarget) {
      this.endPicker = flatpickr(this.endDateInputTarget, {
        ...commonOptions,
        defaultDate: this.endDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.endDateValue = this.formatDate(selectedDates[0]);
            this.presetValue = "custom";
            this.updatePresetHighlight();
            this.updateCalendar();
            this.updateComparisonDates();
          }
        },
      });
    }

    // Compare start date picker
    if (this.hasCompareStartDateInputTarget) {
      this.compareStartPicker = flatpickr(this.compareStartDateInputTarget, {
        ...commonOptions,
        defaultDate: this.compareStartDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.compareStartDateValue = this.formatDate(selectedDates[0]);
            this.compareTypeValue = "custom";
            this.updateCompareTypeHighlight();
          }
        },
      });
    }

    // Compare end date picker
    if (this.hasCompareEndDateInputTarget) {
      this.compareEndPicker = flatpickr(this.compareEndDateInputTarget, {
        ...commonOptions,
        defaultDate: this.compareEndDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.compareEndDateValue = this.formatDate(selectedDates[0]);
            this.compareTypeValue = "custom";
            this.updateCompareTypeHighlight();
          }
        },
      });
    }

    // Inline calendar for visual date selection
    if (this.hasCalendarTarget) {
      this.inlineCalendar = flatpickr(this.calendarTarget, {
        mode: "range",
        inline: true,
        dateFormat: "Y-m-d",
        defaultDate: this.calendarDates(),
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.startDateValue = this.formatDate(selectedDates[0]);
            this.endDateValue = this.formatDate(selectedDates[1]);
            this.presetValue = "custom";
            this.updatePresetHighlight();
            this.updateDateInputs();
            this.updateComparisonDates();
          }
        },
      });
    }
  }

  destroyFlatpickr() {
    if (this.startPicker) this.startPicker.destroy();
    if (this.endPicker) this.endPicker.destroy();
    if (this.compareStartPicker) this.compareStartPicker.destroy();
    if (this.compareEndPicker) this.compareEndPicker.destroy();
    if (this.inlineCalendar) this.inlineCalendar.destroy();
  }

  toggle(event) {
    event.stopPropagation();
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.toggle("show");
    }
  }

  handleClickOutside(event) {
    if (
      !this.element.contains(event.target) &&
      !event.target.closest(".flatpickr-calendar") &&
      this.hasDropdownTarget
    ) {
      this.dropdownTarget.classList.remove("show");
    }
  }

  selectPreset(event) {
    event.preventDefault();
    const preset = event.currentTarget.dataset.preset;
    this.presetValue = preset;

    const dates = this.calculatePresetDates(preset);
    this.startDateValue = dates.start;
    this.endDateValue = dates.end;

    this.updateDateInputs();
    this.updateCalendar();
    this.updatePresetHighlight();

    // This year / Previous year always compare to same range, previous year
    if (preset === "this_year" || preset === "previous_year") {
      this.compareTypeValue = "previous_year";
      this.updateCompareTypeHighlight();
    }
    this.updateComparisonDates();
    this.updateTriggerLabel();
  }

  calculatePresetDates(preset) {
    const today = new Date();
    let start, end;

    switch (preset) {
      case "today":
        start = today;
        end = today;
        break;
      case "yesterday":
        start = this.subtractDays(today, 1);
        end = this.subtractDays(today, 1);
        break;
      case "last_7_days":
        start = this.subtractDays(today, 6);
        end = today;
        break;
      case "last_28_days":
        start = this.subtractDays(today, 27);
        end = today;
        break;
      case "last_30_days":
        start = this.subtractDays(today, 29);
        end = today;
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "last_quarter":
        // Get the first month of the current quarter (0, 3, 6, or 9)
        const currentQuarterStart = Math.floor(today.getMonth() / 3) * 3;
        // Last quarter's end is the day before current quarter starts
        end = new Date(today.getFullYear(), currentQuarterStart, 0);
        // Last quarter's start is 3 months before that end date's month + 1
        start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
        break;
      case "this_year":
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case "previous_year":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case "last_12_months":
        start = this.subtractYears(today, 1);
        end = today;
        break;
      case "all_upcoming":
        start = today;
        end = null;
        break;
      case "next_30_days":
        start = today;
        end = this.subtractDays(today, -29);
        break;
      case "next_3_months":
        start = today;
        end = this.addMonths(today, 3);
        break;
      case "next_12_months":
        start = today;
        end = this.addMonths(today, 12);
        break;
      case "custom":
      default:
        return { start: this.startDateValue, end: this.endDateValue };
    }

    return {
      start: this.formatDate(start),
      end: end ? this.formatDate(end) : "",
    };
  }

  toggleCompare(event) {
    this.compareEnabledValue = event.target.checked;
    this.updateCompareVisibility();
    if (this.compareEnabledValue) {
      this.updateComparisonDates();
    }
  }

  selectCompareType(event) {
    event.preventDefault();
    const compareType = event.currentTarget.dataset.compareType;
    this.compareTypeValue = compareType;
    this.updateCompareTypeHighlight();
    this.updateComparisonDates();
  }

  selectDateMode(event) {
    event.preventDefault();
    const dateMode = event.currentTarget.dataset.dateMode;
    this.dateModeValue = dateMode;
    this.updateDateModeHighlight();
    this.updateTriggerLabel();
  }

  selectFilter(event) {
    event.preventDefault();
    const filterName = event.currentTarget.dataset.filterName;
    const filterValue = event.currentTarget.dataset.filterValue;
    this.filterValues[filterName] = filterValue;
    this.updateFilterHighlight(filterName);
  }

  updateCompareVisibility() {
    if (this.hasCompareSectionTarget) {
      this.compareSectionTarget.classList.toggle(
        "d-none",
        !this.compareEnabledValue
      );
    }
    if (this.hasCompareTypeSectionTarget) {
      this.compareTypeSectionTarget.classList.toggle(
        "d-none",
        !this.compareEnabledValue
      );
    }
  }

  updateComparisonDates() {
    if (!this.compareEnabledValue || this.compareTypeValue === "custom") return;
    // Open-ended range has no end date to compare against
    if (!this.endDateValue) return;

    const startDate = this.parseDate(this.startDateValue);
    const endDate = this.parseDate(this.endDateValue);
    const dates = this.calculateComparisonDates(
      this.compareTypeValue,
      startDate,
      endDate
    );

    this.compareStartDateValue = this.formatDate(dates.start);
    this.compareEndDateValue = this.formatDate(dates.end);

    this.updateCompareDateInputs();
  }

  calculateComparisonDates(type, startDate, endDate) {
    const daysDiff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

    switch (type) {
      case "previous_period_match_dow":
        // Previous period, same days of week (shift by full weeks)
        const weeksToShift = Math.ceil((daysDiff + 1) / 7);
        return {
          start: this.subtractDays(startDate, weeksToShift * 7),
          end: this.subtractDays(endDate, weeksToShift * 7),
        };
      case "previous_period":
        // Immediately preceding period
        return {
          start: this.subtractDays(startDate, daysDiff + 1),
          end: this.subtractDays(startDate, 1),
        };
      case "previous_year":
        // Same dates, previous year
        return {
          start: this.subtractYears(startDate, 1),
          end: this.subtractYears(endDate, 1),
        };
      case "custom":
      default:
        return {
          start: this.parseDate(this.compareStartDateValue),
          end: this.parseDate(this.compareEndDateValue),
        };
    }
  }

  updateDateInputs() {
    if (this.startPicker) {
      this.startPicker.setDate(this.startDateValue, false);
    }
    if (this.endPicker) {
      if (this.endDateValue) {
        this.endPicker.setDate(this.endDateValue, false);
      } else {
        this.endPicker.clear();
      }
    }
  }

  updateCompareDateInputs() {
    if (this.compareStartPicker) {
      this.compareStartPicker.setDate(this.compareStartDateValue, false);
    }
    if (this.compareEndPicker) {
      this.compareEndPicker.setDate(this.compareEndDateValue, false);
    }
  }

  updateCalendar() {
    if (this.inlineCalendar) {
      this.inlineCalendar.setDate(this.calendarDates(), false);
    }
  }

  calendarDates() {
    return this.endDateValue
      ? [this.startDateValue, this.endDateValue]
      : [this.startDateValue];
  }

  updatePresetHighlight() {
    this.presetItemTargets.forEach((item) => {
      const isActive = item.dataset.preset === this.presetValue;
      item.classList.toggle("active", isActive);
      item.classList.toggle("bg-primary", isActive);
      item.classList.toggle("text-white", isActive);
    });
  }

  updateCompareTypeHighlight() {
    this.compareTypeItemTargets.forEach((item) => {
      const isActive = item.dataset.compareType === this.compareTypeValue;
      item.classList.toggle("active", isActive);
      item.classList.toggle("bg-primary", isActive);
      item.classList.toggle("text-white", isActive);
    });
  }

  updateDateModeHighlight() {
    this.dateModeItemTargets.forEach((item) => {
      const isActive = item.dataset.dateMode === this.dateModeValue;
      item.classList.toggle("active", isActive);
      item.classList.toggle("bg-primary", isActive);
      item.classList.toggle("text-white", isActive);
    });
  }

  updateFilterHighlight(filterName) {
    this.filterItemTargets
      .filter((item) => item.dataset.filterName === filterName)
      .forEach((item) => {
        const isActive = item.dataset.filterValue === this.filterValues[filterName];
        item.classList.toggle("active", isActive);
        item.classList.toggle("bg-primary", isActive);
        item.classList.toggle("text-white", isActive);
      });
  }

  updateTriggerLabel() {
    if (this.hasTriggerLabelTarget) {
      const presetLabels = {
        custom: "Custom",
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 days",
        last_28_days: "Last 28 days",
        last_30_days: "Last 30 days",
        this_month: "This month",
        last_month: "Last month",
        last_quarter: "Last quarter",
        this_year: "This year",
        previous_year: "Last year",
        last_12_months: "Last 12 months",
        all_upcoming: "All Upcoming",
        next_30_days: "Next 30 days",
        next_3_months: "Next 3 months",
        next_12_months: "Next 12 months",
      };
      const presetLabel = presetLabels[this.presetValue] || "Custom";
      // Open-ended range (no end date): show only the preset label —
      // formatDisplayDate("") would silently render today's date
      const rangeText = this.endDateValue
        ? ` &nbsp; ${this.formatDisplayDate(this.startDateValue)} - ${this.formatDisplayDate(this.endDateValue)}`
        : "";
      const dateModeInput = this.hasFormTarget
        ? this.formTarget.querySelector('input[name="date_mode"]')
        : null;
      const dateModeBadge = dateModeInput
        ? ` &nbsp;<span class="badge bg-light text-dark border">${this.dateModeValue === "reservation_date" ? "Reservation Date" : "Booked Date"}</span>`
        : "";
      const filterBadges = Object.entries(this.filterValues || {})
        .filter(([, value]) => value !== "")
        .map(([name, value]) => {
          const item = this.filterItemTargets.find(
            (el) => el.dataset.filterName === name && el.dataset.filterValue === value
          );
          const label = item ? item.textContent.trim() : value;
          return `<span class="badge bg-light text-dark border">${label}</span>`;
        })
        .join(" &nbsp;");
      this.triggerLabelTarget.innerHTML = `${presetLabel}${rangeText}${dateModeBadge}${filterBadges ? " &nbsp;" + filterBadges : ""}`;
    }
  }

  apply(event) {
    event.preventDefault();
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove("show");
    }
    this.updateTriggerLabel();

    // Submit the form
    if (this.hasFormTarget) {
      // Update the hidden inputs with current values
      this.formTarget.querySelector('input[name="preset"]').value =
        this.presetValue;
      this.formTarget.querySelector('input[name="start_date"]').value =
        this.startDateValue;
      this.formTarget.querySelector('input[name="end_date"]').value =
        this.endDateValue;
      this.formTarget.querySelector('input[name="compare"]').value = this
        .compareEnabledValue
        ? "true"
        : "false";
      this.formTarget.querySelector('input[name="compare_type"]').value =
        this.compareTypeValue;

      const dateModeInput = this.formTarget.querySelector(
        'input[name="date_mode"]'
      );
      if (dateModeInput) {
        dateModeInput.value = this.dateModeValue;
      }

      if (this.compareEnabledValue) {
        this.formTarget.querySelector(
          'input[name="compare_start_date"]'
        ).value = this.compareStartDateValue;
        this.formTarget.querySelector('input[name="compare_end_date"]').value =
          this.compareEndDateValue;
      } else {
        this.formTarget.querySelector(
          'input[name="compare_start_date"]'
        ).value = "";
        this.formTarget.querySelector('input[name="compare_end_date"]').value =
          "";
      }

      // Write any extra filter values to hidden inputs
      Object.entries(this.filterValues).forEach(([name, value]) => {
        let input = this.formTarget.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          this.formTarget.appendChild(input);
        }
        input.value = value;
      });

      this.formTarget.requestSubmit();
    }
  }

  cancel(event) {
    event.preventDefault();
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove("show");
    }
  }

  // Utility methods
  formatDate(date) {
    if (typeof date === "string") return date;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  formatDisplayDate(dateStr) {
    const date = this.parseDate(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  parseDate(dateStr) {
    if (!dateStr) return new Date();
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  subtractDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  }

  subtractYears(date, years) {
    const result = new Date(date);
    result.setFullYear(result.getFullYear() - years);
    return result;
  }

  addMonths(date, months) {
    const result = new Date(date);
    const day = result.getDate();
    result.setMonth(result.getMonth() + months);
    // Clamp to the last day of the month (matches Ruby's Date#+ month arithmetic)
    if (result.getDate() < day) result.setDate(0);
    return result;
  }
}
